'use client'

import { FC, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/redux'
import PageHeader from 'components/PageHeader'
import { getUserInfo } from 'redux/slices/userInfo'
import { setViewType } from 'redux/slices/appInfo'
import { copyToClipboard, getViewType } from 'shared/helpers'
import ContentWrapper from 'components/ContentWrapper'
import { getArticleDetail } from 'redux/slices/articleDetail'
import { useCustomLinkHtml } from 'hooks/useCustomLinkHtml'
import { sendTrack } from 'shared/tracking'
import { IArticleItem, PageProps, UserType } from 'types/common'
import Header from 'components/Header'
import Footer from 'components/Footer'
import { showMessage } from 'redux/slices/messages'
import { translate } from 'shared/translation'
import useInView from 'hooks/useInView'
import styles from './styles'

type ArticleDetailProps = {
  slug: string
  articleDetail?: IArticleItem
} & PageProps

const ArticleDetail: FC<ArticleDetailProps> = ({ slug, allPageSettings, articleDetail: serverArticleDetail }) => {
  const { ref: impressionRef, isInView, setIsReady } = useInView()
  const {
    data: clientArticleDetail,
    isLoading: isGetArticleDetailLoading,
    code: getArticleDetailCode,
  } = useAppSelector((state) => state.articleDetail)
  const articleDetail = clientArticleDetail || serverArticleDetail
  const { title, thumbnail, content, categories } = articleDetail || {}
  const {
    data: userInfo,
    isLoading: isGetUserInfoLoading,
    code: getUserInfoCode,
  } = useAppSelector((state) => state.userInfo)
  const userType: UserType = userInfo?.level_tag ? 'AFFILIATE' : 'NON_AFFILIATE'
  const settings = allPageSettings.find((pageSettings) => pageSettings.type === userType)?.page_setting_detail
  const viewType = useAppSelector((state) => state.appInfo.viewType)

  const ref = useCustomLinkHtml(content)
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getUserInfo({ isRequireLogin: !serverArticleDetail }))
    if (!serverArticleDetail) {
      dispatch(getArticleDetail({ slug }))
    }
    dispatch(setViewType(getViewType()))

    const handleResize = () => {
      dispatch(setViewType(getViewType()))
    }

    window.addEventListener('resize', handleResize, false)

    return () => {
      window.removeEventListener('resize', handleResize, false)
    }
  }, [dispatch, slug, serverArticleDetail])

  useEffect(() => {
    if (articleDetail && getUserInfoCode !== undefined) {
      setIsReady(true)
    } else {
      setIsReady(false)
    }
  }, [articleDetail, getUserInfoCode, setIsReady])

  useEffect(() => {
    if (articleDetail && getUserInfoCode !== undefined && isInView) {
      sendTrack({
        operation: 'impression',
        pageType: 'landing_site_list_article',
        pageSection: 'ls_article',
        extraData: {
          name_article: title,
          category: categories,
        },
      })
    }
  }, [articleDetail, categories, title, getUserInfoCode, isInView])

  return (
    <ContentWrapper
      onButtonClicked={() => {
        dispatch(getArticleDetail({ slug }))
      }}
      errorCode={getArticleDetailCode}
      isLoading={(!!isGetArticleDetailLoading && !serverArticleDetail) || !!isGetUserInfoLoading}
      showChildWhenLoading={!!serverArticleDetail}
      loadingFullscreen={!!serverArticleDetail}
      minHeightError={400}
    >
      <div
        className="container"
        ref={impressionRef}
      >
        <Header pageSettings={settings} />
        <div className="content">
          <PageHeader
            title={title}
            categoryTags={categories}
          />
          {!!thumbnail && (
            <img
              src={`${process.env.IMAGE_PREFIX}${thumbnail}`}
              className="thumbnail"
              alt={`article ${title} thumbnail`}
            />
          )}
          {!!content && (
            <div
              className="editor-content"
              ref={ref}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}
          {viewType === 'mobile' && (
            <button
              className="copy-link-button"
              onClick={() => {
                copyToClipboard(window.location.href)
                dispatch(showMessage({ type: 'success', content: translate('copy_success') }))
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/link-icon.png`}
                alt="link icon"
                className="link-icon"
              />
              <span>{translate('copy_link')}</span>
            </button>
          )}
          <Footer />
        </div>
        <style jsx>{styles}</style>
      </div>
    </ContentWrapper>
  )
}

export default ArticleDetail
